import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import type { ChatMessage, ChatStatus } from 'tailwind-v4/blocks/chat'
import {
  ASSISTANT_NAME,
  appendToken,
  completeRun,
  deriveStatus,
  historyToTurn,
  isOwnTurn,
  streamingAgentMessage,
  systemMessage,
  turnToChatMessage,
} from './support-chat-mapping'
import config from '@/config'
import { getToken } from '@/utils/AuthTokenHelper'
import type { SupportHistoryMessage } from '@/state/trunk/trunk.types'
import zionApi from '@/state/zion/zion.slice'

/**
 * useSupportChat: the customer side of a live support case, shaped for the
 * `chat` block. A browser-native WebSocket to trunk's `/support/ws` (raw
 * hono node-ws, not socket.io). One case is one thread. The agent streams
 * token by token; staff can join (shown by name) and pause the agent. The
 * customer never sees whispers, the server filters them.
 *
 * The hook owns the socket and the message list. Everything about how a wire
 * message becomes a transcript message lives in `support-chat-mapping.ts`.
 */

export type SupportConnection = 'connecting' | 'open' | 'closed'

/**
 * The agent moving the customer's screen.
 *
 * Not a message: it arrives on the same socket but it is an instruction to the
 * app, not something to render in the transcript. The hook hands it straight
 * to the caller, which decides whether a page change is welcome right now.
 */
export interface SupportNavigateCommand {
  /** Resolved route, no placeholders, e.g. `/org-1/bookings/settings/tables`. */
  path: string
  /** Sidebar breadcrumb for the chip, e.g. `Bookings › Tables`. */
  label: string
  /** Manifest id, so a repeat of the same move can be told from a new one. */
  pageId: string
}

/**
 * Where the customer is standing, sent up with every turn.
 *
 * This is what makes "how do I change this?" answerable: the agent reads the
 * route rather than asking them to describe where they are.
 */
export interface SupportTurnContext {
  path?: string
  venueSerial?: string
}

/**
 * The spoken half, arriving on the SAME socket as the typed one.
 *
 * Deliberately not a second connection: one case, one thread, one
 * transcript, and the existing rules about staff joining and pausing apply
 * to a spoken turn without being reimplemented. See trunk's
 * `services/support-voice.ts`.
 */
export type SupportVoiceState = 'off' | 'connecting' | 'listening' | 'error'

export interface SupportVoiceEvents {
  /** One chunk of the agent's speech, base64 PCM16. */
  onAudio: (chunk: string) => void
  /** Barge-in: the person talked over it, so drop buffered playback. */
  onInterrupt: () => void
  onState: (state: SupportVoiceState) => void
  /**
   * Who decides a turn ended, told to us when the conversation opens.
   *
   * A hosted provider does it; one we drive ourselves cannot, so the
   * browser must. The server knows which provider is configured and the
   * browser does not, so this arrives rather than being guessed.
   */
  onTurnDetection?: (mode: 'provider' | 'client') => void
}

export interface SupportChatApi {
  /** The Mastra thread behind the case. Asset uploads key on this. */
  threadId?: string
  connection: SupportConnection
  /**
   * The case the socket is on. The requested id until trunk answers; then
   * the id in `ready`, which differs from the request only when no id was
   * asked for (trunk resumes the customer's latest open case or creates
   * one). Never the previous case's id while a new request is in flight.
   */
  caseId?: string
  /** True once history has arrived for the case currently requested. */
  historyLoaded: boolean
  messages: ChatMessage[]
  status: ChatStatus
  agentPaused: boolean
  staffPresent: { userId: string; name: string }[]
  typingName: string | null
  send: (text: string, files?: SupportTurnFile[]) => void
  notifyTyping: () => void
  /** Ask trunk to open the spoken half of this case. */
  startVoice: () => void
  stopVoice: () => void
  /** One chunk of microphone audio, base64 PCM16. */
  sendAudio: (chunk: string) => void
  /** One complete utterance, base64 PCM16. */
  sendUtterance: (chunk: string) => void
  /** Cut the agent off mid-sentence. */
  interruptVoice: () => void
  /** Push the current route up without sending a turn. */
  syncContext: () => void
}

/**
 * A part the agent has asked to put on screen.
 *
 * A closed set: the agent names one and passes data, never markup. Mirrors
 * `PartCommand` in trunk's ui-commands.
 */
/**
 * A file sent up with a turn.
 *
 * Already uploaded by the time it gets here: the URL goes through the
 * socket rather than several megabytes of photo. The agent reads it with
 * `read-attachment`, and because typed and spoken share one thread, a
 * file dropped while typing is readable on the next utterance too.
 */
export interface SupportTurnFile {
  /**
   * The `Asset` row's id, NOT a url.
   *
   * The agent reads the bytes server-side with the task IAM role, through
   * `view-attachment` for an image or `fetch-to-scratchpad` for data.
   * Nothing is public and no address is ever handed to a model, because a
   * model that can be handed a url can be talked into being handed
   * somebody else's.
   */
  assetId: string
  name: string
  mediaType: string
  /**
   * A local object URL, for showing the file back in the transcript.
   *
   * Never sent to trunk and never given to the agent: the real bytes are
   * private and read server-side by asset id.
   */
  previewUrl?: string
}

export interface SupportPart {
  kind: 'choose'
  prompt: string
  options: { label: string; detail?: string }[]
}

/**
 * A diagram the agent drew.
 *
 * STRUCTURE, NOT GEOMETRY: the layout happens in the browser with dagre.
 * See `support-dock/diagram/scene.ts` for why it is this way round.
 */
export interface SupportDiagramCommand {
  title?: string
  direction?: 'TB' | 'LR'
  nodes: { id: string; label: string; kind?: string; group?: string }[]
  edges: { from: string; to: string; label?: string }[]
}

export interface SupportPartCommand {
  part: SupportPart
  mode: 'panel' | 'stage'
  height: 'peek' | 'half' | 'full'
}

export interface UseSupportChatOptions {
  orgId: string
  /** Case to join. Omit to let trunk resume the latest open case or create one. */
  caseId?: string
  /** The agent wants a part on screen. Omit and parts are ignored. */
  onPart?: (command: SupportPartCommand) => void
  /** The agent drew a diagram. Omit and diagrams are ignored. */
  onDiagram?: (command: SupportDiagramCommand) => void
  /**
   * Start a NEW conversation rather than resuming the last open one.
   *
   * The voice orb sets it. The orb is opened with a keypress to ask one
   * thing, so inheriting the previous session meant it answered from stale
   * context: greeting somebody with the page they were on an hour earlier,
   * re-reading a lookup nobody had asked for again.
   *
   * Only ever sent on the FIRST connect. Once trunk answers with a case id
   * the socket reconnects by that id, so a dropped connection mid-sentence
   * rejoins what it was on instead of starting over. Typed support is a
   * case somebody comes back to, so it leaves this off.
   */
  fresh?: boolean
  /** The current user's Morpheus uid, to tell their own turns from a colleague's. */
  myUid?: string
  /** False keeps the socket closed (the list view, before a case is picked). */
  enabled?: boolean
  /**
   * Where the customer currently is. Read at SEND time through a ref, so the
   * caller can pass a fresh object every render without reconnecting the
   * socket, and so the route that goes up is the one they were on when they
   * pressed enter rather than the one they were on when the hook mounted.
   */
  turnContext?: SupportTurnContext
  /**
   * The agent wants to move them. Called once per command; the caller owns
   * the decision and the actual navigation. Read through a ref for the same
   * reason as `turnContext`.
   */
  onNavigate?: (command: SupportNavigateCommand) => void
  /** Where the spoken half reports to. Omit and voice is simply unused. */
  voice?: SupportVoiceEvents
}

interface ServerMsg {
  type: string
  // ready
  caseId?: string
  threadId?: string
  agentPaused?: boolean
  // history
  messages?: SupportHistoryMessage[]
  // message
  role?: string
  text?: string
  visibility?: string
  authorId?: string
  authorName?: string
  functionalRole?: string
  // stream
  runId?: string
  delta?: string
  // presence / typing / status / error
  event?: 'joined' | 'left'
  userId?: string
  name?: string
  by?: string
  message?: string
  // navigate
  path?: string
  label?: string
  pageId?: string
  // refresh
  tags?: string[]
  reason?: string
  // diagram
  title?: string
  nodes?: { id: string; label: string; kind?: string; group?: string }[]
  edges?: { from: string; to: string; label?: string }[]
  direction?: 'TB' | 'LR'
  // part
  part?: SupportPart
  mode?: 'panel' | 'stage'
  height?: 'peek' | 'half' | 'full'
  // voice
  state?: SupportVoiceState
  data?: string
  turnDetection?: 'provider' | 'client'
}

const THINKING_TIMEOUT_MS = 60_000
const TYPING_TIMEOUT_MS = 4_000
const RECONNECT_CAP_MS = 15_000
/** Server-side close code for an auth rejection. Permanent, do not retry. */
const CLOSE_FORBIDDEN = 4001

function wsBase(): string {
  return config.url.trunk.replace(/^http/, 'ws')
}

const now = () => new Date().toISOString()
const newId = () => crypto.randomUUID()

export function useSupportChat(opts: UseSupportChatOptions): SupportChatApi {
  const { orgId, enabled = true } = opts
  const [connection, setConnection] = useState<SupportConnection>(
    enabled ? 'connecting' : 'closed'
  )
  // What trunk answered, tagged with the request it answered. Selectors below
  // only trust it while it matches the current request, so a stale answer
  // from the previous case never leaks into one render of the next.
  const [resolved, setResolved] = useState<{
    requested?: string
    caseId: string
  } | null>(null)
  /**
   * The Mastra thread behind the case.
   *
   * Exposed because it is what the ASSET routes key on: an upload is
   * presigned against a thread, not a case, and `view-attachment` reads
   * an asset row that belongs to one. The case id is the conversation's
   * name; the thread id is where its messages and files live.
   */
  const [threadId, setThreadId] = useState<string | undefined>(undefined)
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [agentPaused, setAgentPaused] = useState(false)
  const [staffPresent, setStaffPresent] = useState<
    { userId: string; name: string }[]
  >([])
  const [typingName, setTypingName] = useState<string | null>(null)
  // True from `agent_thinking` until the first token, `done` or an error. The
  // agent often runs tools for several seconds before any text.
  const [thinking, setThinking] = useState(false)

  const wsRef = useRef<WebSocket | null>(null)
  // The case the socket joins (overwritten by `ready` so a reconnect rejoins
  // the resolved case) and the route's request at connect time.
  const caseIdRef = useRef<string | undefined>(opts.caseId)
  const requestedRef = useRef<string | undefined>(opts.caseId)
  // Through a ref like everything else `handle` touches: rebuilding the
  // message handler would tear the socket down and back up.
  const dispatch = useDispatch()
  const dispatchRef = useRef(dispatch)
  dispatchRef.current = dispatch
  // Read through a ref so a late-arriving uid does not reconnect the socket.
  const myUidRef = useRef(opts.myUid)
  myUidRef.current = opts.myUid
  // Same: `connect` must not be rebuilt when this flips, or the socket
  // restarts underneath a live conversation.
  const freshRef = useRef(opts.fresh)
  freshRef.current = opts.fresh
  // Same reason, and additionally so the route that goes up with a turn is
  // the one they are on NOW, not the one the socket was opened on.
  const turnContextRef = useRef(opts.turnContext)
  turnContextRef.current = opts.turnContext
  const onNavigateRef = useRef(opts.onNavigate)
  onNavigateRef.current = opts.onNavigate
  const onPartRef = useRef(opts.onPart)
  onPartRef.current = opts.onPart
  const onDiagramRef = useRef(opts.onDiagram)
  onDiagramRef.current = opts.onDiagram
  const voiceRef = useRef(opts.voice)
  voiceRef.current = opts.voice
  const closedByUs = useRef(false)
  const retryRef = useRef(0)
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const thinkingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopThinking = useCallback(() => {
    if (thinkingTimer.current) clearTimeout(thinkingTimer.current)
    thinkingTimer.current = null
    setThinking(false)
  }, [])

  const append = useCallback((m: ChatMessage) => {
    setMessages((prev) => [...prev, m])
  }, [])

  const note = useCallback(
    (text: string) => {
      append(systemMessage(newId(), text, now()))
    },
    [append]
  )

  /** Close the current socket without triggering its reconnect. */
  const teardown = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current)
    reconnectTimer.current = null
    const ws = wsRef.current
    // `onclose` checks this and stays quiet for a socket we replaced.
    wsRef.current = null
    try {
      ws?.close()
    } catch {
      /* noop */
    }
  }, [])

  const reset = useCallback(() => {
    setMessages([])
    setResolved(null)
    setHistoryLoaded(false)
    setAgentPaused(false)
    setStaffPresent([])
    setTypingName(null)
    stopThinking()
    retryRef.current = 0
  }, [stopThinking])

  const handle = useCallback(
    (msg: ServerMsg) => {
      switch (msg.type) {
        case 'ready':
          if (msg.threadId) setThreadId(msg.threadId)
          if (msg.caseId) {
            caseIdRef.current = msg.caseId
            setResolved({ requested: requestedRef.current, caseId: msg.caseId })
          }
          setAgentPaused(Boolean(msg.agentPaused))
          break
        case 'history':
          setMessages(
            (msg.messages ?? []).map((m) =>
              turnToChatMessage(historyToTurn(m), myUidRef.current)
            )
          )
          setHistoryLoaded(true)
          break
        case 'message': {
          const turn = {
            id: newId(),
            role: msg.role,
            functionalRole: msg.functionalRole,
            authorId: msg.authorId,
            authorName: msg.authorName,
            text: msg.text ?? '',
            createdAt: now(),
          }
          // Our own turns come back as echoes too; we already added them
          // when we sent. A colleague's turn in a shared case is kept.
          if (isOwnTurn(turn, myUidRef.current)) break
          append(turnToChatMessage(turn, myUidRef.current))
          break
        }
        case 'navigate': {
          // An instruction to the app, not a line of transcript. The agent
          // says where it has taken them in its own next sentence, so adding
          // a note here would say it twice.
          const { path, label, pageId } = msg
          if (!path || !label || !pageId) break
          onNavigateRef.current?.({ path, label, pageId })
          break
        }
        case 'refresh': {
          /**
           * The agent changed something the page is showing.
           *
           * It can build a floor plan or rename a booking type while the
           * customer watches the very page that lists them, and without
           * this they are told "done" over a screen still showing the old
           * thing. Pressing refresh to see work the agent says it did is
           * the point at which somebody stops believing it.
           *
           * The tags are Connect's own, chosen in trunk's `refresh-tags`
           * from the list in zion.slice. An unknown one matches no query
           * and quietly does nothing, which is the right failure: a cache
           * that refetches too little is a stale screen, a cache told to
           * throw away the wrong thing is every screen flickering.
           */
          if (!msg.tags?.length) break
          dispatchRef.current(zionApi.util.invalidateTags(msg.tags as never))
          break
        }
        case 'diagram': {
          // An instruction to the canvas, not a line of transcript: the
          // agent says what it drew in its own next sentence.
          if (!msg.nodes?.length) break
          onDiagramRef.current?.({
            title: msg.title,
            direction: msg.direction ?? 'TB',
            nodes: msg.nodes,
            edges: msg.edges ?? [],
          })
          break
        }
        case 'part': {
          // An instruction to the surface, not a line of transcript: the
          // agent says the options out loud in the same turn, so adding a
          // message here would say them twice.
          if (!msg.part || msg.part.kind !== 'choose') break
          onPartRef.current?.({
            part: msg.part,
            mode: msg.mode ?? 'panel',
            height: msg.height ?? 'half',
          })
          break
        }
        case 'voice_audio':
          if (msg.data) voiceRef.current?.onAudio(msg.data)
          break
        case 'voice_interrupt':
          voiceRef.current?.onInterrupt()
          break
        case 'voice_status':
          if (msg.turnDetection) {
            voiceRef.current?.onTurnDetection?.(msg.turnDetection)
          }
          // 'off' covers both a deliberate stop and the provider hanging up;
          // the pill shows the same thing either way.
          voiceRef.current?.onState(msg.state ?? 'off')
          if (msg.state === 'error' && msg.message) note(msg.message)
          break
        case 'voice_transcript':
          // The spoken words come back through the ordinary `message` path
          // too, written to the case by trunk, so nothing is rendered here.
          // This exists for a live caption later.
          break
        case 'agent_thinking':
          setThinking(true)
          if (thinkingTimer.current) clearTimeout(thinkingTimer.current)
          thinkingTimer.current = setTimeout(() => {
            setThinking(false)
          }, THINKING_TIMEOUT_MS)
          break
        case 'agent_start': {
          stopThinking()
          const runId = msg.runId
          if (!runId) break
          setMessages((prev) =>
            prev.some((m) => m.id === runId)
              ? prev
              : [...prev, streamingAgentMessage(runId, '', now())]
          )
          break
        }
        case 'token': {
          const { runId, delta } = msg
          if (!runId || !delta) break
          stopThinking()
          setMessages((prev) => appendToken(prev, runId, delta, now()))
          break
        }
        case 'done':
          stopThinking()
          setMessages((prev) => completeRun(prev, msg.runId))
          break
        case 'status':
          setAgentPaused(Boolean(msg.agentPaused))
          note(
            msg.agentPaused
              ? `${msg.by ?? 'A teammate'} is now helping you directly.`
              : `Handed back to the ${ASSISTANT_NAME}.`
          )
          break
        case 'presence': {
          const { userId, name } = msg
          if (msg.role !== 'staff' || !userId || !name) break
          if (msg.event === 'joined') {
            setStaffPresent((p) =>
              p.some((s) => s.userId === userId) ? p : [...p, { userId, name }]
            )
            note(`${name} from Stampede joined the chat.`)
          } else if (msg.event === 'left') {
            setStaffPresent((p) => p.filter((s) => s.userId !== userId))
          }
          break
        }
        case 'typing':
          if (!msg.name || msg.role === 'customer') break
          setTypingName(msg.name)
          if (typingTimer.current) clearTimeout(typingTimer.current)
          typingTimer.current = setTimeout(() => {
            setTypingName(null)
          }, TYPING_TIMEOUT_MS)
          break
        case 'error':
          stopThinking()
          setMessages((prev) => completeRun(prev))
          note(
            msg.message ?? 'Something went wrong. A teammate will follow up.'
          )
          break
        default:
          break
      }
    },
    [append, note, stopThinking]
  )

  const connect = useCallback(() => {
    const token = getToken()?.accessToken
    if (!token) {
      setConnection('closed')
      return
    }
    const params = new URLSearchParams({ token, orgId })
    // A known case always wins, which is what makes a reconnect a rejoin
    // rather than a new conversation. `fresh` can only apply before trunk
    // has told us one.
    if (caseIdRef.current) params.set('caseId', caseIdRef.current)
    else if (freshRef.current) params.set('fresh', '1')
    const ws = new WebSocket(`${wsBase()}/support/ws?${params.toString()}`)
    wsRef.current = ws
    setConnection('connecting')

    ws.onopen = () => {
      retryRef.current = 0
      setConnection('open')
    }

    ws.onmessage = (ev) => {
      let msg: ServerMsg
      try {
        msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '')
      } catch {
        return
      }
      handle(msg)
    }

    ws.onclose = (event) => {
      // A newer socket replaced this one, or we tore it down: stay quiet, or
      // two live sockets would double-append every message.
      if (wsRef.current !== ws) return
      setConnection('closed')
      if (closedByUs.current || event.code === CLOSE_FORBIDDEN) return
      const delay = Math.min(1000 * 2 ** retryRef.current, RECONNECT_CAP_MS)
      retryRef.current += 1
      reconnectTimer.current = setTimeout(() => {
        if (!closedByUs.current && wsRef.current === ws) connect()
      }, delay)
    }

    ws.onerror = () => {
      try {
        ws.close()
      } catch {
        /* noop */
      }
    }
  }, [orgId, handle])

  // Which case the socket should be on. Re-runs when the route changes case.
  useEffect(() => {
    if (!enabled) {
      closedByUs.current = true
      teardown()
      reset()
      setConnection('closed')
      return
    }
    const wanted = opts.caseId
    const ws = wsRef.current
    const alive = ws !== null && ws.readyState <= WebSocket.OPEN
    // The URL was just pinned to the id trunk resolved: same case, keep the socket.
    if (alive && wanted && caseIdRef.current === wanted) return
    teardown()
    reset()
    caseIdRef.current = wanted
    requestedRef.current = wanted
    closedByUs.current = false
    connect()
  }, [orgId, enabled, opts.caseId, connect, reset, teardown])

  // Unmount: close everything, drop timers.
  useEffect(
    () => () => {
      closedByUs.current = true
      teardown()
      if (typingTimer.current) clearTimeout(typingTimer.current)
      if (thinkingTimer.current) clearTimeout(thinkingTimer.current)
    },
    [teardown]
  )

  const send = useCallback(
    (text: string, files?: SupportTurnFile[]) => {
      const trimmed = text.trim()
      // A turn can be nothing but files: dropping a floor plan in and
      // saying nothing is a complete request.
      if (!trimmed && !files?.length) return
      const ws = wsRef.current
      if (!ws || ws.readyState !== WebSocket.OPEN) return
      ws.send(
        JSON.stringify({
          type: 'message',
          text: trimmed,
          context: turnContextRef.current,
          ...(files?.length ? { files } : {}),
        })
      )
      /**
       * Optimistic, and it has to carry the files.
       *
       * The server's echo of this turn does not: the attachment block it
       * writes is an instruction for the agent, not something to print,
       * so the only place the thumbnails can come from is here. Without
       * this you attach three images, press send, and the transcript
       * shows the sentence with nothing beside it.
       */
      append({
        id: newId(),
        role: 'user',
        parts: [
          ...(trimmed ? [{ type: 'text' as const, text: trimmed }] : []),
          ...(files ?? []).map((file) => ({
            type: 'attachment' as const,
            attachment: {
              id: file.assetId,
              filename: file.name,
              mimeType: file.mediaType,
              // The local preview the composer already made. The real
              // bytes are private; this is only for showing it back.
              url: file.previewUrl ?? '',
              uploadStatus: 'ready' as const,
            },
          })),
        ],
        createdAt: now(),
        status: 'complete',
      })
    },
    [append]
  )

  /** Raw send, for the voice controls and the context ping. */
  const post = useCallback((payload: Record<string, unknown>) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload))
  }, [])

  const startVoice = useCallback(() => {
    post({ type: 'voice_start', context: turnContextRef.current })
  }, [post])
  const stopVoice = useCallback(() => {
    post({ type: 'voice_stop' })
  }, [post])
  /** One chunk of microphone audio. The hot path: keep it this thin. */
  const sendAudio = useCallback(
    (chunk: string) => {
      post({ type: 'voice_audio', data: chunk })
    },
    [post]
  )
  /** One complete utterance, when the browser owns turn detection. */
  const sendUtterance = useCallback(
    (chunk: string) => {
      post({ type: 'voice_utterance', data: chunk })
    },
    [post]
  )
  /** They talked over the agent: stop it saying the rest. */
  const interruptVoice = useCallback(() => {
    post({ type: 'voice_interrupt' })
  }, [post])

  /**
   * Tell trunk where they are now, with no turn attached.
   *
   * A spoken conversation outlives several pages, including the ones the
   * agent moved them to. Without this, "what is this setting?" is answered
   * about wherever they were standing when they pressed the microphone.
   */
  const syncContext = useCallback(() => {
    post({ type: 'context', context: turnContextRef.current })
  }, [post])

  const notifyTyping = useCallback(() => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'typing' }))
    }
  }, [])

  // Trust the answer only while it is for the current request: the request
  // it answered, or (after the URL is pinned to the answer) the answer itself.
  const current =
    resolved !== null &&
    (resolved.requested === opts.caseId || resolved.caseId === opts.caseId)

  return {
    connection,
    caseId: current ? resolved.caseId : opts.caseId,
    threadId,
    historyLoaded: current && historyLoaded,
    messages,
    status: deriveStatus(thinking, messages),
    agentPaused,
    staffPresent,
    typingName,
    send,
    notifyTyping,
    startVoice,
    stopVoice,
    sendAudio,
    sendUtterance,
    interruptVoice,
    syncContext,
  }
}
